import React, { useEffect, useState } from 'react';
import { 
  Dialog, DialogContent, 
  IconButton, Typography, Box, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import apiFetch from '../utils/apiFetch';

const TimeUtilizationReport = ({ open, onClose, userId, userName }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totals, setTotals] = useState({ activeMs: 0, stoppedMs: 0, totalInterviews: 0 });

  useEffect(() => {
    if (open && userId) {
      // Clear previous data immediately so stale data doesn't flash
      setData([]);
      setTotals({ activeMs: 0, stoppedMs: 0, totalInterviews: 0 });
      setLoading(true);
      setError(null);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/usercallingstatus/logindetails/${userId}`;
      
      apiFetch(apiUrl, { method: "GET", headers: { authtoken: localStorage.getItem("loggedintoken") } })
        .then(res => res.status === 200 ? res.json() : res.text())
        .then(repos => {
          if (repos && repos.data && Array.isArray(repos.data) && repos.data.length > 0) {
            const sortedData = repos.data.sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
            const processedData = [];
            let totalActiveMs = 0;
            let totalStoppedMs = 0;
            let totalInterviewsCount = 0; // Running total for the header
            
            for (let i = 0; i < sortedData.length; i++) {
              const currentRecord = sortedData[i];
              
              const activeStart = new Date(currentRecord.startTime);
              const activeEnd = currentRecord.stopTime ? new Date(currentRecord.stopTime) : new Date();
              totalActiveMs += Math.abs(activeEnd - activeStart);

              const interviewCount = currentRecord.Interviews || 0;
              totalInterviewsCount += interviewCount; // Add to running total

              // 1. Push Active Record
              processedData.push({
                _id: currentRecord._id + '-active',
                isCallingActive: true,
                startTime: currentRecord.startTime,
                stopTime: currentRecord.stopTime,
                stopComments: `${interviewCount} Interviews`
              });
              
              // 2. Push Stopped/Gap Record (Only if not a final logout)
              if (currentRecord.stopTime) {
                let gapEndTime = (i + 1 < sortedData.length) ? sortedData[i + 1].startTime : null;
                
                // Fix: Check if this was a final logout
                const cleanComment = currentRecord.stopComments ? currentRecord.stopComments.toLowerCase().trim() : "";
                const isFinalLogout = cleanComment.includes("log out") || cleanComment.includes("logout");
                
                // Skip adding the stopped row if it's the final logout of the day with no subsequent sessions
                const shouldSkipGapRow = isFinalLogout && !gapEndTime;

                if (!shouldSkipGapRow && (!gapEndTime || new Date(gapEndTime) > new Date(currentRecord.stopTime))) {
                  const stoppedStart = new Date(currentRecord.stopTime);
                  const stoppedEnd = gapEndTime ? new Date(gapEndTime) : new Date();
                  totalStoppedMs += Math.abs(stoppedEnd - stoppedStart);

                  processedData.push({
                    _id: currentRecord._id + '-stopped',
                    isCallingActive: false,
                    startTime: currentRecord.stopTime,
                    stopTime: gapEndTime,
                    stopComments: currentRecord.stopComments
                  });
                }
              }
            }
            
            // Save all totals to state
            setTotals({ 
              activeMs: totalActiveMs, 
              stoppedMs: totalStoppedMs, 
              totalInterviews: totalInterviewsCount 
            });
            setData(processedData);
          } else {
            // Ensure data stays empty if API returns nothing
            setData([]);
            setTotals({ activeMs: 0, stoppedMs: 0, totalInterviews: 0 });
          }
          setLoading(false);
        })
        .catch(err => {
          setError(err.toString());
          setLoading(false);
        });
    } else if (!open) {
      // Clear data when dialog closes
      setData([]);
      setTotals({ activeMs: 0, stoppedMs: 0, totalInterviews: 0 });
    }
  }, [open, userId]);

  const formatDuration = (ms) => {
    if (ms <= 0) return "0h 0m";
    const mins = Math.floor(ms / 60000);
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  const calculateDuration = (start, stop) => {
    if (!start) return "-";
    const startTime = new Date(start);
    const stopTime = stop ? new Date(stop) : new Date();
    return formatDuration(Math.abs(stopTime - startTime));
  };

  const formatTime = (isoString) => {
    if (!isoString) return "-";
    return new Date(isoString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, overflow: 'hidden' }
      }}
    >
      {/* PREMIUM MUI HEADER */}
      <Box sx={{ 
        background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)', 
        color: 'white', 
        px: 3, 
        py: 2.5,
        display: 'flex', 
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        position: 'relative',
        zIndex: 1
      }}>
        
        {/* Left Side: Title & Subtitle */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.5, lineHeight: 1.2 }}>
            Time Utilization Report
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5, fontWeight: 500 }}>
            Agent: {userName}
          </Typography>
        </Box>
        
        {/* Right Side: Stats & Close Button */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          
          {!loading && !error && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              
              {/* Interviews Stat Pill */}
              <Box sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.95)', 
                borderRadius: '8px', 
                px: 2, py: 0.75,
                display: 'flex', alignItems: 'center', gap: 1.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <Typography sx={{ color: '#546e7a', fontSize: '0.7rem', fontWeight: 800, letterSpacing: 0.5 }}>
                  INTERVIEWS
                </Typography>
                <Typography sx={{ color: '#0288d1', fontSize: '1.1rem', fontWeight: 800 }}>
                  {totals.totalInterviews}
                </Typography>
              </Box>

              {/* Active Stat Pill */}
              <Box sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.95)', 
                borderRadius: '8px', 
                px: 2, py: 0.75,
                display: 'flex', alignItems: 'center', gap: 1.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <Typography sx={{ color: '#546e7a', fontSize: '0.7rem', fontWeight: 800, letterSpacing: 0.5 }}>
                  ACTIVE
                </Typography>
                <Typography sx={{ color: '#2e7d32', fontSize: '1.1rem', fontWeight: 800 }}>
                  {formatDuration(totals.activeMs)}
                </Typography>
              </Box>

              {/* Stopped Stat Pill */}
              <Box sx={{ 
                bgcolor: 'rgba(255, 255, 255, 0.95)', 
                borderRadius: '8px', 
                px: 2, py: 0.75,
                display: 'flex', alignItems: 'center', gap: 1.5,
                boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
              }}>
                <Typography sx={{ color: '#546e7a', fontSize: '0.7rem', fontWeight: 800, letterSpacing: 0.5 }}>
                  STOPPED
                </Typography>
                <Typography sx={{ color: '#d32f2f', fontSize: '1.1rem', fontWeight: 800 }}>
                  {formatDuration(totals.stoppedMs)}
                </Typography>
              </Box>
              
            </Box>
          )}
          
          <IconButton 
            aria-label="close" 
            onClick={onClose} 
            sx={{ 
              color: "white", 
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.25)' },
              transition: 'background-color 0.2s ease'
            }}
          >
            <CloseIcon />
          </IconButton>
          
        </Box>
      </Box>
      
      {/* DIALOG CONTENT */}
      <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
        {loading ? (
          <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" py={8}>
            <CircularProgress size={40} thickness={4} />
            <Typography sx={{ mt: 2, color: 'text.secondary', fontWeight: 500 }}>
              Loading utilization data...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Typography color="error" variant="h6">{error}</Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Table size="medium">
              <TableHead sx={{ backgroundColor: "#f1f5f9" }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Start Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Stop Time</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Duration</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: '#334155' }}>Comments</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row) => (
                  <TableRow 
                    key={row._id} 
                    sx={{ 
                      backgroundColor: 'white',
                      '&:hover': { backgroundColor: '#f8fafc' },
                      transition: 'background-color 0.2s ease',
                      '& td': { borderBottom: '1px solid #f1f5f9' }
                    }}
                  >
                    <TableCell>
                      <Box sx={{ 
                        display: 'inline-flex', 
                        alignItems: 'center', 
                        px: 1.5, 
                        py: 0.5, 
                        borderRadius: 1,
                        bgcolor: row.isCallingActive ? '#edf7ed' : '#fdeded',
                        color: row.isCallingActive ? '#2e7d32' : '#d32f2f',
                        fontWeight: 600,
                        fontSize: '0.85rem'
                      }}>
                        {row.isCallingActive ? "Active" : "Stopped"}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{formatTime(row.startTime)}</TableCell>
                    <TableCell sx={{ color: '#475569', fontWeight: 500 }}>{formatTime(row.stopTime)}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#0f172a' }}>{calculateDuration(row.startTime, row.stopTime)}</TableCell>
                    <TableCell sx={{ color: row.stopComments ? '#334155' : '#94a3b8' }}>
                      {row.stopComments || "-"}
                    </TableCell>
                  </TableRow>
                ))}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6, color: '#64748b' }}>
                      <Typography variant="body1">No utilization records found for today.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TimeUtilizationReport;